import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { TapeDeckVideo } from './TapeDeckVideo';
import './VoiceNotePage.css';
import { isGuestVoiceNotesConfigured, submitGuestVoiceNote } from './guestVoiceNote';
import { readVoiceNoteSubmissionContext } from './voiceNoteUrlContext';
import {
  decodeBlobToAudioBuffer,
  isIosLikeDevice,
  pickRecorderMimeType,
  playBufferTapeRewind,
  reverseAudioBuffer,
} from './voiceNoteAudio';
import {
  preloadVoiceNoteDeckAssets,
  recorderPublicUrl,
  VOICE_NOTE_AUTO_REWIND_FILE,
  VOICE_NOTE_CLICK_DEPRESS_FILE,
  VOICE_NOTE_CLICK_RELEASE_FILE,
} from './voiceNoteAssets';

/** @param {unknown} err */
function microphoneHelpFromError(err) {
  const name =
    err && typeof err === 'object' && 'name' in err
      ? /** @type {{ name?: string }} */ (err).name
      : '';
  const ios = isIosLikeDevice();
  if (name === 'NotAllowedError' || name === 'PermissionDeniedError') {
    return {
      title: 'Microphone access denied',
      body: ios
        ? 'Allow the microphone for this site: tap “aA” in the address bar → Website Settings → Microphone → Allow. Or open Settings → Safari → Microphone and enable access, then return and tap Record again.'
        : 'Allow microphone access when your browser asks, or open site settings (lock or tune icon in the address bar), allow the mic, then tap Record again.',
    };
  }
  if (name === 'NotFoundError' || name === 'DevicesNotFoundError') {
    return {
      title: 'No microphone found',
      body: 'This device doesn’t have a usable microphone, or the browser can’t see it.',
    };
  }
  if (name === 'NotReadableError' || name === 'TrackStartError' || name === 'AbortError') {
    return {
      title: 'Microphone is busy or interrupted',
      body: 'Another app or tab may be using the mic. Close it, then tap Record again.',
    };
  }
  if (name === 'SecurityError' || name === 'NotSupportedError') {
    return {
      title: 'Recording isn’t available here',
      body: ios
        ? 'Use Safari on a normal (non-private) tab where possible, and open this page over HTTPS.'
        : 'Use a secure (https) link in an up-to-date browser.',
    };
  }
  return {
    title: 'Could not use the microphone',
    body: ios
      ? 'Check Safari’s microphone permission for this site, then tap Record again.'
      : 'Check browser permissions and try Record again.',
  };
}

const ASPECT_W = 1648;
const ASPECT_H = 2093;

const REWIND_WALL_SEC = 3;

/**
 * Master trim into `destination` for UI SFX (clicks, rewind bed + scrub). Unity lets beds/scrub sit
 * next to transport clicks (those get a separate `VOICE_NOTE_CLICK_SFX_GAIN`).
 */
const VOICE_NOTE_SFX_BUS_GAIN = 1;
/** Hot MP3 clicks — product with bus ≈ perceived “reference” vs rewind + voice. */
const VOICE_NOTE_CLICK_SFX_GAIN = 0.52;
/** auto-rewind.mp3 after the bus (file is mastered quiet vs clicks). */
const AUTO_REWIND_SAMPLE_GAIN = 2.4;
/**
 * Reverse scrub is decoded mic audio; buck a bit above unity so it reads next to the bed and
 * transport (playbackRate ramp also affects perceived level).
 */
const TAPE_SCRUB_LEVEL = 2.84;
/**
 * iOS `MediaElementAudioSource` → destination (~2× prior step; still shy of system vol on device).
 */
const VOICE_NOTE_FORWARD_PLAYBACK_IOS_GAIN = 12.5;

/** Show rewind/play pressed art when tapped with no recording (ms). */
const EMPTY_TRANSPORT_FLASH_MS = 400;

/** Match CSS fade-in duration before counting full-opacity hold. */
const REC_RECORD_PROMPT_FADE_IN_MS = 520;
const REC_RECORD_PROMPT_VISIBLE_MS = 3000;
/** Match CSS fade-out duration. */
const REC_RECORD_PROMPT_FADE_OUT_MS = 550;
const REC_RECORD_PROMPT_IN_DELAY_MS = 50;

/** Pause after clip is ready so transport chrome can settle before the submit control appears. */
const SEND_BTN_REVEAL_DELAY_MS = 360;

/** REC uses click-only chrome (no pointer “hold”); spacing between depress and release SFX. */
const REC_CLICK_SOUND_RELEASE_MS = 95;

/** @param {number} px */
function rowPct(px) {
  return `${(px / ASPECT_H) * 100}%`;
}

/** @param {number} px */
function colPct(px) {
  return `${(px / ASPECT_W) * 100}%`;
}

function recorderSrc(filename) {
  return recorderPublicUrl(filename);
}

/**
 * @typedef {'idle' | 'rewinding' | 'playing' | 'paused'} PlayoutPhase
 */

export function VoiceNotePage() {
  const [isMicRecording, setIsMicRecording] = useState(false);
  const [recordedBlob, setRecordedBlob] = useState(/** @type {Blob | null} */ (null));
  const [playoutPhase, setPlayoutPhase] = useState(/** @type {PlayoutPhase} */ ('idle'));
  const [uploadStatus, setUploadStatus] = useState(/** @type {'idle' | 'sending' | 'sent' | 'error'} */ ('idle'));
  const [uploadError, setUploadError] = useState(/** @type {string | null} */ (null));
  const [micHint, setMicHint] = useState(/** @type {{ title: string; body: string } | null} */ (null));
  const [deckAssetsReady, setDeckAssetsReady] = useState(false);
  const [deckRevealVisible, setDeckRevealVisible] = useState(false);
  const [emptyTransportFlash, setEmptyTransportFlash] = useState(
    /** @type {null | 'rewindplay' | 'play'} */ (null),
  );
  const [recRecordPromptPhase, setRecRecordPromptPhase] = useState(
    /** @type {'off' | 'in' | 'visible' | 'out'} */ ('off'),
  );
  const [sendBtnEntered, setSendBtnEntered] = useState(false);

  const firebaseVoiceNotesReady = useMemo(() => isGuestVoiceNotesConfigured(), []);
  const submissionContext = useMemo(() => readVoiceNoteSubmissionContext(), []);

  const clickDepressAudioRef = useRef(/** @type {HTMLAudioElement | null} */ (null));
  const clickReleaseAudioRef = useRef(/** @type {HTMLAudioElement | null} */ (null));
  /** Decoded auto-rewind.mp3 (Web Audio — survives async after tap; HTMLAudio often does not). */
  const autoRewindBufferRef = useRef(/** @type {AudioBuffer | null} */ (null));
  /** Transport click SFX as buffers — HTMLAudio from `ended` / rewind callbacks is often silent on iOS. */
  const clickDepressBufferRef = useRef(/** @type {AudioBuffer | null} */ (null));
  const clickReleaseBufferRef = useRef(/** @type {AudioBuffer | null} */ (null));
  const autoRewindSampleSourceRef = useRef(/** @type {AudioBufferSourceNode | null} */ (null));

  const playoutSessionRef = useRef(0);
  /** Wall-clock cap for tape rewind (AudioContext `onended` can miss if suspended / rate quirks). */
  const rewindWatchdogRef = useRef(0);
  const emptyFlashTimerRef = useRef(0);
  const recPromptTimersRef = useRef(/** @type {number[]} */ ([]));
  const sendBtnEnterTimerRef = useRef(0);
  /** After stopping the mic via Play/Rewind, run this transport action once `recordedBlob` is ready. */
  const transportAfterStopRef = useRef(/** @type {null | 'play' | 'rewind'} */ (null));
  /** True after REC pointerdown played depress; cleared when release (sound 2) plays. */
  const recClickSoundTimerRef = useRef(0);
  const mediaRecorderRef = useRef(/** @type {MediaRecorder | null} */ (null));
  const streamRef = useRef(/** @type {MediaStream | null} */ (null));
  const chunksRef = useRef(/** @type {Blob[]} */ ([]));

  const audioContextRef = useRef(/** @type {AudioContext | null} */ (null));
  /** One gain into `destination` for all UI SFX; rebuilt if the `AudioContext` instance changes. */
  const sfxBusGainRef = useRef(/** @type {GainNode | null} */ (null));
  const rewindSourceRef = useRef(/** @type {AudioBufferSourceNode | null} */ (null));
  const forwardAudioRef = useRef(/** @type {HTMLAudioElement | null} */ (null));

  const decodedBlobRef = useRef(/** @type {Blob | null} */ (null));
  const forwardBufferRef = useRef(/** @type {AudioBuffer | null} */ (null));
  const reverseBufferRef = useRef(/** @type {AudioBuffer | null} */ (null));

  const forwardObjectUrlRef = useRef(/** @type {string | null} */ (null));
  const forwardEndedRef = useRef(/** @type {null | (() => void)} */ (null));
  /** iOS: `createMediaElementSource` is once per `<audio>` element for a given context. */
  const forwardMediaElementTapWiredRef = useRef(false);

  const clearForwardEnded = useCallback(() => {
    const el = forwardAudioRef.current;
    const fn = forwardEndedRef.current;
    if (el && fn) el.removeEventListener('ended', fn);
    forwardEndedRef.current = null;
  }, []);

  const invalidateDecodeCache = useCallback(() => {
    decodedBlobRef.current = null;
    forwardBufferRef.current = null;
    reverseBufferRef.current = null;
  }, []);

  const revokeForwardUrl = useCallback(() => {
    if (forwardObjectUrlRef.current) {
      URL.revokeObjectURL(forwardObjectUrlRef.current);
      forwardObjectUrlRef.current = null;
    }
  }, []);

  const clearRewindWatchdog = useCallback(() => {
    if (rewindWatchdogRef.current) {
      window.clearTimeout(rewindWatchdogRef.current);
      rewindWatchdogRef.current = 0;
    }
  }, []);

  const stopTapeRewindScrub = useCallback(() => {
    try {
      rewindSourceRef.current?.stop(0);
    } catch {
      /* already stopped */
    }
    rewindSourceRef.current = null;
  }, []);

  /** Stops scrub + auto-rewind.mp3 (e.g. new rewind, full reset). Tail may still play if only scrub stops — see `stopTapeRewindScrub`. */
  const stopRewindSound = useCallback(() => {
    stopTapeRewindScrub();
    try {
      autoRewindSampleSourceRef.current?.stop(0);
    } catch {
      /* already stopped */
    }
    autoRewindSampleSourceRef.current = null;
  }, [stopTapeRewindScrub]);

  const ensureAutoRewindSampleDecoded = useCallback(async (/** @type {AudioContext} */ ctx) => {
    if (autoRewindBufferRef.current) return autoRewindBufferRef.current;
    const url = recorderPublicUrl(VOICE_NOTE_AUTO_REWIND_FILE);
    const res = await fetch(url);
    if (!res.ok) throw new Error(`auto-rewind ${res.status}`);
    const arr = await res.arrayBuffer();
    const decoded = await ctx.decodeAudioData(arr.slice(0));
    autoRewindBufferRef.current = decoded;
    return decoded;
  }, []);

  const ensureAudioContext = useCallback(async () => {
    const AC = window.AudioContext || window.webkitAudioContext;
    if (!audioContextRef.current) {
      audioContextRef.current = new AC();
    }
    const ctx = audioContextRef.current;
    /* `interrupted` exists on iOS; treat anything other than `running` like suspended. */
    if (ctx.state !== 'running') {
      try {
        await ctx.resume();
      } catch {
        /* ignore */
      }
    }
    return ctx;
  }, []);

  const getSfxBus = useCallback((/** @type {AudioContext} */ ctx) => {
    let g = sfxBusGainRef.current;
    if (!g || g.context !== ctx) {
      g = ctx.createGain();
      g.gain.value = VOICE_NOTE_SFX_BUS_GAIN;
      g.connect(ctx.destination);
      sfxBusGainRef.current = g;
    }
    return g;
  }, []);

  const ensureForwardMediaElementTap = useCallback(
    async (/** @type {HTMLAudioElement} */ el) => {
      if (!isIosLikeDevice() || forwardMediaElementTapWiredRef.current) return;
      const ctx = await ensureAudioContext();
      if (ctx.state !== 'running') {
        try {
          await ctx.resume();
        } catch {
          /* ignore */
        }
      }
      try {
        const mes = ctx.createMediaElementSource(el);
        const g = ctx.createGain();
        g.gain.value = VOICE_NOTE_FORWARD_PLAYBACK_IOS_GAIN;
        mes.connect(g);
        g.connect(ctx.destination);
        forwardMediaElementTapWiredRef.current = true;
      } catch {
        /* Leave default element output (e.g. strict mode / already wired). */
      }
    },
    [ensureAudioContext],
  );

  const ensureClickBuffersDecoded = useCallback(async (/** @type {AudioContext} */ ctx) => {
    if (clickDepressBufferRef.current && clickReleaseBufferRef.current) return;
    if (!clickDepressBufferRef.current) {
      const res = await fetch(recorderPublicUrl(VOICE_NOTE_CLICK_DEPRESS_FILE));
      if (!res.ok) throw new Error(`click depress ${res.status}`);
      const arr = await res.arrayBuffer();
      clickDepressBufferRef.current = await ctx.decodeAudioData(arr.slice(0));
    }
    if (!clickReleaseBufferRef.current) {
      const res = await fetch(recorderPublicUrl(VOICE_NOTE_CLICK_RELEASE_FILE));
      if (!res.ok) throw new Error(`click release ${res.status}`);
      const arr = await res.arrayBuffer();
      clickReleaseBufferRef.current = await ctx.decodeAudioData(arr.slice(0));
    }
  }, []);

  /** @returns whether Web Audio played (otherwise caller may fall back to HTMLAudio). */
  const playClickViaWebAudio = useCallback(
    async (/** @type {'depress' | 'release'} */ kind) => {
      try {
        const ctx = await ensureAudioContext();
        if (ctx.state !== 'running') await ctx.resume();
        await ensureClickBuffersDecoded(ctx);
        const buf =
          kind === 'depress' ? clickDepressBufferRef.current : clickReleaseBufferRef.current;
        if (!buf) return false;
        const bus = getSfxBus(ctx);
        const trim = ctx.createGain();
        trim.gain.value = VOICE_NOTE_CLICK_SFX_GAIN;
        trim.connect(bus);
        const src = ctx.createBufferSource();
        src.buffer = buf;
        src.connect(trim);
        src.start();
        return true;
      } catch {
        return false;
      }
    },
    [ensureAudioContext, ensureClickBuffersDecoded, getSfxBus],
  );

  /** Keep Web Audio tied to the user gesture on first pointerdown (important on iOS). */
  const primeWebAudioOnGesture = useCallback(() => {
    const AC = window.AudioContext || window.webkitAudioContext;
    if (!audioContextRef.current && AC) {
      audioContextRef.current = new AC();
    }
    const ctx = audioContextRef.current;
    if (ctx && ctx.state !== 'running') void ctx.resume();
  }, []);

  const playClickDepress = useCallback(() => {
    void playClickViaWebAudio('depress').then((ok) => {
      if (ok) return;
      const a = clickDepressAudioRef.current;
      if (!a) return;
      try {
        /* iOS sometimes ignores initial `volume` until after first interaction; re-apply each play. */
        a.volume = VOICE_NOTE_SFX_BUS_GAIN * VOICE_NOTE_CLICK_SFX_GAIN;
        a.currentTime = 0;
        void a.play();
      } catch {
        /* ignore */
      }
    });
  }, [playClickViaWebAudio]);

  const playClickRelease = useCallback(() => {
    void playClickViaWebAudio('release').then((ok) => {
      if (ok) return;
      const a = clickReleaseAudioRef.current;
      if (!a) return;
      try {
        a.volume = VOICE_NOTE_SFX_BUS_GAIN * VOICE_NOTE_CLICK_SFX_GAIN;
        a.currentTime = 0;
        void a.play();
      } catch {
        /* ignore */
      }
    });
  }, [playClickViaWebAudio]);

  /**
   * Play/Rewind transport only: synchronous `HTMLAudio` so the first tap works on iOS Safari before any
   * async Web Audio decode (gesture-gated playback). REC still uses `playClickDepress`/`Release` only
   * after mic permission where we want deferred SFX.
   */
  const playTransportClickDepress = useCallback(() => {
    const a = clickDepressAudioRef.current;
    if (!a) return;
    try {
      a.volume = VOICE_NOTE_SFX_BUS_GAIN * VOICE_NOTE_CLICK_SFX_GAIN;
      a.currentTime = 0;
      void a.play();
    } catch {
      /* ignore */
    }
  }, []);

  const playTransportClickRelease = useCallback(() => {
    const a = clickReleaseAudioRef.current;
    if (!a) return;
    try {
      a.volume = VOICE_NOTE_SFX_BUS_GAIN * VOICE_NOTE_CLICK_SFX_GAIN;
      a.currentTime = 0;
      void a.play();
    } catch {
      /* ignore */
    }
  }, []);

  /** Play/Rewind: normal tap is depress→release; while REC is armed, reverse (release→depress) like tape review. */
  const transportTapPointerDown = useCallback(() => {
    primeWebAudioOnGesture();
    if (isMicRecording) playTransportClickRelease();
    else playTransportClickDepress();
  }, [isMicRecording, playTransportClickDepress, playTransportClickRelease, primeWebAudioOnGesture]);

  const transportTapPointerUp = useCallback(() => {
    if (isMicRecording) playTransportClickDepress();
    else playTransportClickRelease();
  }, [isMicRecording, playTransportClickDepress, playTransportClickRelease]);

  const playRecTapSounds = useCallback(() => {
    primeWebAudioOnGesture();
    if (recClickSoundTimerRef.current) {
      window.clearTimeout(recClickSoundTimerRef.current);
      recClickSoundTimerRef.current = 0;
    }
    playClickDepress();
    recClickSoundTimerRef.current = window.setTimeout(() => {
      recClickSoundTimerRef.current = 0;
      playClickRelease();
    }, REC_CLICK_SOUND_RELEASE_MS);
  }, [playClickDepress, playClickRelease, primeWebAudioOnGesture]);

  const stopPlayoutAndResetChrome = useCallback(() => {
    playoutSessionRef.current += 1;
    clearRewindWatchdog();
    stopRewindSound();
    clearForwardEnded();
    const a = forwardAudioRef.current;
    if (a) {
      a.pause();
      a.removeAttribute('src');
      a.load();
    }
    revokeForwardUrl();
    setPlayoutPhase('idle');
  }, [clearForwardEnded, clearRewindWatchdog, revokeForwardUrl, stopRewindSound]);

  const clearRecPromptTimers = useCallback(() => {
    recPromptTimersRef.current.forEach((id) => window.clearTimeout(id));
    recPromptTimersRef.current = [];
  }, []);

  /** @param {number} [delayMs] Wait this long before fading in (after empty Play/Rewind chrome de-press). */
  const showRecRecordPrompt = useCallback((delayMs = 0) => {
    clearRecPromptTimers();
    const armSequence = () => {
      recPromptTimersRef.current = [];
      setRecRecordPromptPhase('in');
      recPromptTimersRef.current.push(
        window.setTimeout(() => setRecRecordPromptPhase('visible'), REC_RECORD_PROMPT_IN_DELAY_MS),
        window.setTimeout(
          () => setRecRecordPromptPhase('out'),
          REC_RECORD_PROMPT_IN_DELAY_MS +
            REC_RECORD_PROMPT_FADE_IN_MS +
            REC_RECORD_PROMPT_VISIBLE_MS,
        ),
        window.setTimeout(() => {
          setRecRecordPromptPhase('off');
          clearRecPromptTimers();
        },
          REC_RECORD_PROMPT_IN_DELAY_MS +
            REC_RECORD_PROMPT_FADE_IN_MS +
            REC_RECORD_PROMPT_VISIBLE_MS +
            REC_RECORD_PROMPT_FADE_OUT_MS),
      );
    };
    if (delayMs > 0) {
      recPromptTimersRef.current.push(window.setTimeout(armSequence, delayMs));
    } else {
      armSequence();
    }
  }, [clearRecPromptTimers]);

  useEffect(() => () => {
    if (emptyFlashTimerRef.current) window.clearTimeout(emptyFlashTimerRef.current);
    clearRecPromptTimers();
    if (sendBtnEnterTimerRef.current) {
      window.clearTimeout(sendBtnEnterTimerRef.current);
      sendBtnEnterTimerRef.current = 0;
    }
    transportAfterStopRef.current = null;
    if (recClickSoundTimerRef.current) {
      window.clearTimeout(recClickSoundTimerRef.current);
      recClickSoundTimerRef.current = 0;
    }
    stopPlayoutAndResetChrome();
    streamRef.current?.getTracks().forEach((t) => t.stop());
    mediaRecorderRef.current?.stop();
    audioContextRef.current?.close().catch(() => {});
    audioContextRef.current = null;
    sfxBusGainRef.current = null;
    forwardMediaElementTapWiredRef.current = false;
    autoRewindBufferRef.current = null;
    clickDepressBufferRef.current = null;
    clickReleaseBufferRef.current = null;
    invalidateDecodeCache();
  }, [clearRecPromptTimers, invalidateDecodeCache, stopPlayoutAndResetChrome]);

  const triggerEmptyTransportFlash = useCallback((/** @type {'rewindplay' | 'play'} */ mode) => {
    if (emptyFlashTimerRef.current) window.clearTimeout(emptyFlashTimerRef.current);
    setEmptyTransportFlash(mode);
    emptyFlashTimerRef.current = window.setTimeout(() => {
      emptyFlashTimerRef.current = 0;
      setEmptyTransportFlash(null);
    }, EMPTY_TRANSPORT_FLASH_MS);
  }, []);

  useEffect(() => {
    if (playoutPhase === 'idle') return;
    if (emptyFlashTimerRef.current) {
      window.clearTimeout(emptyFlashTimerRef.current);
      emptyFlashTimerRef.current = 0;
    }
    setEmptyTransportFlash(null);
  }, [playoutPhase]);

  useEffect(() => {
    const html = document.documentElement;
    const body = document.body;
    const root = document.getElementById('root');
    const prevHtml = html.style.overflow;
    const prevBody = body.style.overflow;
    const prevOverscroll = body.style.overscrollBehavior;
    const prevRoot = root?.style.overflow ?? '';
    html.style.overflow = 'hidden';
    body.style.overflow = 'hidden';
    body.style.overscrollBehavior = 'none';
    if (root) root.style.overflow = 'hidden';
    return () => {
      if (prevHtml) html.style.overflow = prevHtml;
      else html.style.removeProperty('overflow');
      if (prevBody) body.style.overflow = prevBody;
      else body.style.removeProperty('overflow');
      if (prevOverscroll) body.style.overscrollBehavior = prevOverscroll;
      else body.style.removeProperty('overscroll-behavior');
      if (root) {
        if (prevRoot) root.style.overflow = prevRoot;
        else root.style.removeProperty('overflow');
      }
    };
  }, []);

  useEffect(() => {
    const down = new Audio(recorderPublicUrl(VOICE_NOTE_CLICK_DEPRESS_FILE));
    const up = new Audio(recorderPublicUrl(VOICE_NOTE_CLICK_RELEASE_FILE));
    down.preload = 'auto';
    up.preload = 'auto';
    const clickHtmlVol = VOICE_NOTE_SFX_BUS_GAIN * VOICE_NOTE_CLICK_SFX_GAIN;
    down.volume = clickHtmlVol;
    up.volume = clickHtmlVol;
    clickDepressAudioRef.current = down;
    clickReleaseAudioRef.current = up;
    return () => {
      clickDepressAudioRef.current = null;
      clickReleaseAudioRef.current = null;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    void preloadVoiceNoteDeckAssets().then(() => {
      if (cancelled) return;
      setDeckAssetsReady(true);
      requestAnimationFrame(() => {
        if (!cancelled) setDeckRevealVisible(true);
      });
    });
    return () => {
      cancelled = true;
    };
  }, []);

  const ensureDecoded = useCallback(async () => {
    if (!recordedBlob) return null;
    const ctx = await ensureAudioContext();
    if (decodedBlobRef.current === recordedBlob && forwardBufferRef.current && reverseBufferRef.current) {
      return forwardBufferRef.current;
    }
    const buf = await decodeBlobToAudioBuffer(ctx, recordedBlob);
    const rev = reverseAudioBuffer(ctx, buf);
    forwardBufferRef.current = buf;
    reverseBufferRef.current = rev;
    decodedBlobRef.current = recordedBlob;
    return buf;
  }, [ensureAudioContext, recordedBlob]);

  useEffect(() => {
    if (!recordedBlob) return;
    void ensureDecoded().catch(() => {});
  }, [recordedBlob, ensureDecoded]);

  const attachForwardElement = useCallback(() => {
    const el = forwardAudioRef.current;
    if (!el || !recordedBlob) return;
    revokeForwardUrl();
    const url = URL.createObjectURL(recordedBlob);
    forwardObjectUrlRef.current = url;
    el.src = url;
    el.load();
  }, [recordedBlob, revokeForwardUrl]);

  const beginRewindThenAutoplay = useCallback(async () => {
    if (!recordedBlob) return;
    clearRewindWatchdog();
    playoutSessionRef.current += 1;
    const session = playoutSessionRef.current;
    setPlayoutPhase('rewinding');

    try {
      const preCtx = audioContextRef.current;
      if (preCtx && preCtx.state !== 'running') await preCtx.resume();
    } catch {
      /* ignore */
    }

    stopRewindSound();
    clearForwardEnded();
    const el = forwardAudioRef.current;
    if (el) {
      el.pause();
      el.currentTime = 0;
    }

    let ctx;
    try {
      ctx = await ensureAudioContext();
      await ensureDecoded();
    } catch {
      setPlayoutPhase('idle');
      return;
    }

    if (playoutSessionRef.current !== session) {
      return;
    }

    const rev = reverseBufferRef.current;
    if (!rev || !ctx) {
      setPlayoutPhase('idle');
      return;
    }

    let rewindToPlayDone = false;
    const finishRewindToPlaying = () => {
      if (rewindToPlayDone) return;
      rewindToPlayDone = true;
      clearRewindWatchdog();
      /* Keep auto-rewind.mp3 running — tail SFX continues while forward playback starts. */
      stopTapeRewindScrub();
      if (playoutSessionRef.current !== session) return;
      playClickRelease();
      setPlayoutPhase('playing');
      attachForwardElement();
      const audio = forwardAudioRef.current;
      if (!audio) return;
      clearForwardEnded();
      audio.currentTime = 0;
      const onEnded = () => {
        if (playoutSessionRef.current !== session) return;
        forwardEndedRef.current = null;
        playClickRelease();
        setPlayoutPhase('idle');
      };
      forwardEndedRef.current = onEnded;
      audio.addEventListener('ended', onEnded);
      audio.volume = 1;
      void (async () => {
        try {
          if (playoutSessionRef.current !== session) return;
          await ensureForwardMediaElementTap(audio);
          const ctx = await ensureAudioContext();
          if (playoutSessionRef.current !== session) return;
          if (ctx.state !== 'running') await ctx.resume();
          await audio.play();
        } catch {
          setPlayoutPhase('idle');
        }
      })();
    };

    const watchdogMs = REWIND_WALL_SEC * 1000 + 500;
    rewindWatchdogRef.current = window.setTimeout(() => {
      rewindWatchdogRef.current = 0;
      if (rewindToPlayDone) return;
      if (playoutSessionRef.current !== session) return;
      finishRewindToPlaying();
    }, watchdogMs);

    try {
      await ensureAutoRewindSampleDecoded(ctx);
    } catch {
      /* still play tape-only rewind */
    }

    if (playoutSessionRef.current !== session) {
      return;
    }

    /*
     * Safari iOS: after forward `<audio>` playback (and even after `await` on a “warm” decode),
     * the context often drops out of `running`. Scheduling rewind sources while suspended is silent;
     * the watchdog still fires so playback continues with no rewind SFX (second tap symptom).
     */
    try {
      if (ctx.state !== 'running') await ctx.resume();
    } catch {
      /* ignore */
    }

    const sfxBus = getSfxBus(ctx);
    const t0 = ctx.currentTime;
    const sampleBuf = autoRewindBufferRef.current;
    if (sampleBuf) {
      try {
        const samp = ctx.createBufferSource();
        samp.buffer = sampleBuf;
        const arGain = ctx.createGain();
        arGain.gain.value = AUTO_REWIND_SAMPLE_GAIN;
        samp.connect(arGain);
        arGain.connect(sfxBus);
        samp.onended = () => {
          if (autoRewindSampleSourceRef.current === samp) autoRewindSampleSourceRef.current = null;
        };
        samp.start(t0);
        autoRewindSampleSourceRef.current = samp;
      } catch {
        /* decode / routing */
      }
    }

    const scrubToSfx = ctx.createGain();
    scrubToSfx.gain.value = TAPE_SCRUB_LEVEL / VOICE_NOTE_SFX_BUS_GAIN;
    scrubToSfx.connect(sfxBus);
    rewindSourceRef.current = playBufferTapeRewind(ctx, rev, REWIND_WALL_SEC, () => {
      rewindSourceRef.current = null;
      finishRewindToPlaying();
    }, scrubToSfx);
  }, [
    attachForwardElement,
    clearForwardEnded,
    clearRewindWatchdog,
    ensureAudioContext,
    ensureAutoRewindSampleDecoded,
    ensureDecoded,
    ensureForwardMediaElementTap,
    getSfxBus,
    playClickRelease,
    recordedBlob,
    stopTapeRewindScrub,
    stopRewindSound,
  ]);

  const runPlayTransportAction = useCallback(async () => {
    if (playoutPhase === 'playing') {
      forwardAudioRef.current?.pause();
      setPlayoutPhase('paused');
      return;
    }
    if (playoutPhase === 'paused') {
      try {
        await ensureAudioContext();
        await forwardAudioRef.current?.play();
        setPlayoutPhase('playing');
      } catch {
        setPlayoutPhase('idle');
      }
      return;
    }
    if (playoutPhase === 'rewinding') return;
    await beginRewindThenAutoplay();
  }, [beginRewindThenAutoplay, ensureAudioContext, playoutPhase]);

  const onRecClick = useCallback(async () => {
    if (isMicRecording) {
      playRecTapSounds();
      mediaRecorderRef.current?.stop();
      return;
    }

    const { hostname, protocol } = window.location;
    const localHost =
      hostname === 'localhost' ||
      hostname === '127.0.0.1' ||
      hostname === '[::1]';
    /*
     * iOS Safari omits `navigator.mediaDevices` on plain http:// unless the host is treated as a
     * secure context (e.g. localhost on that device). Hitting REC on `http://<LAN-IP>` then fails the
     * getUserMedia guard first and showed a bogus “update iOS / use Safari” hint — check transport first.
     */
    if (protocol !== 'https:' && !localHost) {
      setMicHint({
        title: 'Secure connection required',
        body: isIosLikeDevice()
          ? 'Safari is fine — the mic only works on HTTPS here (not on http:// plus your Wi‑Fi IP). Serve the dev site over HTTPS, use a tunnel (ngrok, Cloudflare Tunnel, etc.), or test recording on the deployed https:// site.'
          : 'Open this page with HTTPS to record. Most browsers block the microphone on plain http.',
      });
      return;
    }

    if (typeof navigator === 'undefined' || !navigator.mediaDevices?.getUserMedia) {
      setMicHint({
        title: 'Recording isn’t supported',
        body: isIosLikeDevice()
          ? 'This Safari version doesn’t expose the microphone API (try updating iOS), or use a non‑private tab.'
          : 'Try another browser or device; recording needs microphone support.',
      });
      return;
    }

    primeWebAudioOnGesture();

    stopPlayoutAndResetChrome();

    if (recordedBlob) {
      setRecordedBlob(null);
      invalidateDecodeCache();
    }

    try {
      let stream;
      try {
        /*
         * iOS often speaker-ducks all page audio after a “call-quality” mic session; lighter
         * constraints reduce that vs aggressive AEC/NS at the cost of raw recording polish.
         */
        stream = await navigator.mediaDevices.getUserMedia({
          audio: isIosLikeDevice()
            ? { echoCancellation: false, noiseSuppression: false }
            : { echoCancellation: true, noiseSuppression: true },
        });
      } catch (e) {
        if (
          e &&
          typeof e === 'object' &&
          'name' in e &&
          /** @type {{ name?: string }} */ (e).name === 'OverconstrainedError'
        ) {
          stream = await navigator.mediaDevices.getUserMedia({ audio: true });
        } else {
          throw e;
        }
      }
      playRecTapSounds();
      streamRef.current = stream;
      chunksRef.current = [];
      setMicHint(null);
      const mime = pickRecorderMimeType();
      const mr = new MediaRecorder(
        stream,
        mime ? { mimeType: mime } : undefined,
      );
      mediaRecorderRef.current = mr;
      mr.ondataavailable = (ev) => {
        if (ev.data.size > 0) chunksRef.current.push(ev.data);
      };
      mr.onstop = () => {
        const fallbackType = isIosLikeDevice() ? 'audio/mp4' : 'audio/webm';
        const blob = new Blob(chunksRef.current, { type: mr.mimeType || fallbackType });
        stream.getTracks().forEach((t) => t.stop());
        streamRef.current = null;
        mediaRecorderRef.current = null;
        /* Play/Rewind during REC: arm rewind in the same batch as blob + mic off so the deck
         * never gets an idle frame (idle fires useIdleDeckVideoFirstFrame play() vs hardStop()). */
        if (transportAfterStopRef.current) {
          setPlayoutPhase('rewinding');
        }
        setIsMicRecording(false);
        setRecordedBlob(blob);
      };
      const timesliceMs = isIosLikeDevice() ? 250 : 100;
      mr.start(timesliceMs);
      setIsMicRecording(true);
    } catch (err) {
      setIsMicRecording(false);
      setMicHint(microphoneHelpFromError(err));
    }
  }, [
    invalidateDecodeCache,
    isMicRecording,
    playRecTapSounds,
    primeWebAudioOnGesture,
    recordedBlob,
    stopPlayoutAndResetChrome,
  ]);

  const onPlayClick = useCallback(async () => {
    if (isMicRecording) {
      transportAfterStopRef.current = 'play';
      if (mediaRecorderRef.current?.state === 'recording') {
        mediaRecorderRef.current.stop();
      }
      return;
    }
    primeWebAudioOnGesture();
    if (!recordedBlob) {
      triggerEmptyTransportFlash('play');
      showRecRecordPrompt(EMPTY_TRANSPORT_FLASH_MS);
      return;
    }
    await ensureAudioContext();
    await runPlayTransportAction();
  }, [
    ensureAudioContext,
    isMicRecording,
    primeWebAudioOnGesture,
    recordedBlob,
    runPlayTransportAction,
    showRecRecordPrompt,
    triggerEmptyTransportFlash,
  ]);

  const onRewindClick = useCallback(async () => {
    if (isMicRecording) {
      transportAfterStopRef.current = 'rewind';
      if (mediaRecorderRef.current?.state === 'recording') {
        mediaRecorderRef.current.stop();
      }
      return;
    }
    primeWebAudioOnGesture();
    if (!recordedBlob) {
      triggerEmptyTransportFlash('rewindplay');
      showRecRecordPrompt(EMPTY_TRANSPORT_FLASH_MS);
      return;
    }
    await ensureAudioContext();
    await beginRewindThenAutoplay();
  }, [
    beginRewindThenAutoplay,
    ensureAudioContext,
    isMicRecording,
    primeWebAudioOnGesture,
    recordedBlob,
    showRecRecordPrompt,
    triggerEmptyTransportFlash,
  ]);

  useEffect(() => {
    if (isMicRecording || !recordedBlob) return;
    const pending = transportAfterStopRef.current;
    if (!pending) return;
    transportAfterStopRef.current = null;
    void (async () => {
      primeWebAudioOnGesture();
      await ensureAudioContext();
      /* Not `runPlayTransportAction`: onstop already sets `rewinding`, and that helper returns early there. */
      await beginRewindThenAutoplay();
    })();
  }, [beginRewindThenAutoplay, ensureAudioContext, isMicRecording, primeWebAudioOnGesture, recordedBlob]);

  useEffect(() => {
    const onBlur = () => {
      if (recClickSoundTimerRef.current) {
        window.clearTimeout(recClickSoundTimerRef.current);
        recClickSoundTimerRef.current = 0;
      }
    };
    window.addEventListener('blur', onBlur);
    return () => window.removeEventListener('blur', onBlur);
  }, []);

  useEffect(() => {
    setUploadStatus('idle');
    setUploadError(null);
  }, [recordedBlob]);

  useEffect(() => {
    if (!recordedBlob) return;
    clearRecPromptTimers();
    setRecRecordPromptPhase('off');
  }, [recordedBlob, clearRecPromptTimers]);

  useEffect(() => {
    if (sendBtnEnterTimerRef.current) {
      window.clearTimeout(sendBtnEnterTimerRef.current);
      sendBtnEnterTimerRef.current = 0;
    }
    if (!firebaseVoiceNotesReady || !recordedBlob) {
      setSendBtnEntered(false);
      return undefined;
    }
    setSendBtnEntered(false);
    sendBtnEnterTimerRef.current = window.setTimeout(() => {
      sendBtnEnterTimerRef.current = 0;
      setSendBtnEntered(true);
    }, SEND_BTN_REVEAL_DELAY_MS);
    return () => {
      if (sendBtnEnterTimerRef.current) {
        window.clearTimeout(sendBtnEnterTimerRef.current);
        sendBtnEnterTimerRef.current = 0;
      }
    };
  }, [firebaseVoiceNotesReady, recordedBlob]);

  const onSendVoiceNote = useCallback(async () => {
    if (!recordedBlob || isMicRecording || uploadStatus === 'sending') return;
    setUploadStatus('sending');
    setUploadError(null);
    try {
      await submitGuestVoiceNote(recordedBlob, submissionContext);
      setUploadStatus('sent');
    } catch (e) {
      setUploadError(e instanceof Error ? e.message : 'Could not send');
      setUploadStatus('error');
    }
  }, [isMicRecording, recordedBlob, submissionContext, uploadStatus]);

  /** @type {'inactive' | 'rewindplay' | 'play'} */
  const transportMode =
    emptyTransportFlash ??
    (playoutPhase === 'rewinding' ? 'rewindplay' : playoutPhase === 'playing' ? 'play' : 'inactive');
  const showRecPressed = isMicRecording;
  /** LED on only while recording. */
  const showLed = isMicRecording;

  /**
   * Deck motor: forward for REC capstan + listen playback; reverse only during auto-rewind.
   * Rewind must win if it ever overlaps recording (e.g. batched updates) — otherwise forward runs first in the video hook and reverse never applies.
   */
  const tapeReverse = playoutPhase === 'rewinding';
  const tapeForward =
    !tapeReverse && (isMicRecording || playoutPhase === 'playing');

  const showSendVoiceNote = Boolean(firebaseVoiceNotesReady && recordedBlob);
  const hasBottomSubmitZone = showSendVoiceNote || recRecordPromptPhase !== 'off';

  return (
    <div className={'voice-note-page' + (hasBottomSubmitZone ? ' voice-note-page--has-submit' : '')}>
      {!deckAssetsReady ? (
        <div className="voice-note-page__load-overlay" aria-busy="true" aria-live="polite">
          <div className="voice-note-page__load-spinner" />
          <span className="voice-note-page__load-sr">Loading recorder…</span>
        </div>
      ) : null}
      <div
        className={
          'voice-note-page__reveal-root' + (deckRevealVisible ? ' voice-note-page__reveal-root--visible' : '')
        }
      >
      <div className="voice-note-page__main">
        {micHint ? (
          <div className="voice-note-page__mic-hint" role="status" aria-live="polite">
            <strong className="voice-note-page__mic-hint-title">{micHint.title}</strong>
            <p className="voice-note-page__mic-hint-body">{micHint.body}</p>
            <button
              type="button"
              className="voice-note-page__mic-hint-dismiss"
              onPointerDown={() => {
                primeWebAudioOnGesture();
                playClickDepress();
              }}
              onPointerUp={() => playClickRelease()}
              onPointerCancel={() => playClickRelease()}
              onClick={() => setMicHint(null)}
            >
              Dismiss
            </button>
          </div>
        ) : null}
        <div className="voice-note-page__deck-wrap">
          <div className="voice-note-page__rig">
          <div
            className="tape-recorder-frame"
            style={{ aspectRatio: `${ASPECT_W} / ${ASPECT_H}` }}
          >
          <TapeDeckVideo tapeReverse={tapeReverse} tapeForward={tapeForward} />
          <img
            className="tape-recorder-layer tape-recorder-layer--base"
            src={recorderSrc('tape-recorder-BG.png')}
            alt=""
          />

          <div className="tape-recorder-transport-stack" aria-hidden>
            <img
              className={
                'tape-recorder-transport-stack__img' +
                (transportMode === 'inactive' ? ' tape-recorder-transport-stack__img--visible' : '')
              }
              src={recorderSrc('tape-recorder-buttons-inactive.png')}
              alt=""
            />
            <img
              className={
                'tape-recorder-transport-stack__img' +
                (transportMode === 'rewindplay' ? ' tape-recorder-transport-stack__img--visible' : '')
              }
              src={recorderSrc('rewindplay-pressed.png')}
              alt=""
            />
            <img
              className={
                'tape-recorder-transport-stack__img' +
                (transportMode === 'play' ? ' tape-recorder-transport-stack__img--visible' : '')
              }
              src={recorderSrc('play-pressed.png')}
              alt=""
            />
          </div>

          {showRecPressed ? (
            <img
              className="tape-recorder-layer tape-recorder-layer--chrome"
              src={recorderSrc('rec-pressed.png')}
              alt=""
            />
          ) : null}

          {showLed ? (
            <img
              className="tape-recorder-layer tape-recorder-layer--chrome tape-recorder-layer--led voice-note-led-flicker"
              src={recorderSrc('LED-on.png')}
              alt=""
            />
          ) : null}

          <div className="tape-recorder-hit-layer">
            <table className="tape-recorder-slice-table" role="presentation">
              <colgroup>
                <col style={{ width: colPct(459) }} />
                <col style={{ width: colPct(228) }} />
                <col style={{ width: colPct(207) }} />
                <col style={{ width: colPct(411) }} />
                <col style={{ width: colPct(343) }} />
              </colgroup>
              <tbody>
                <tr className="tape-recorder-slice-row" style={{ height: rowPct(1468) }}>
                  <td className="tape-recorder-slice-cell tape-recorder-slice-cell--inert" colSpan={5} />
                </tr>
                <tr className="tape-recorder-slice-row" style={{ height: rowPct(222) }}>
                  <td className="tape-recorder-slice-cell tape-recorder-slice-cell--inert" colSpan={3} />
                  <td className="tape-recorder-slice-cell tape-recorder-hit" rowSpan={2} id="rec">
                    <button
                      type="button"
                      className="tape-recorder-hit-btn"
                      aria-label={isMicRecording ? 'Stop recording' : 'Record'}
                      aria-pressed={isMicRecording}
                      onClick={() => void onRecClick()}
                    />
                  </td>
                  <td className="tape-recorder-slice-cell tape-recorder-slice-cell--inert" rowSpan={4} />
                </tr>
                <tr className="tape-recorder-slice-row" style={{ height: rowPct(189) }}>
                  <td className="tape-recorder-slice-cell tape-recorder-slice-cell--inert" rowSpan={3} />
                  <td className="tape-recorder-slice-cell tape-recorder-hit" rowSpan={2} id="rewind">
                    <button
                      type="button"
                      className="tape-recorder-hit-btn"
                      aria-label="Rewind"
                      aria-pressed={
                        playoutPhase === 'rewinding' || emptyTransportFlash === 'rewindplay'
                      }
                      onPointerDown={transportTapPointerDown}
                      onPointerUp={transportTapPointerUp}
                      onPointerCancel={transportTapPointerUp}
                      onClick={() => void onRewindClick()}
                    />
                  </td>
                  <td className="tape-recorder-slice-cell tape-recorder-hit" rowSpan={2} id="play">
                    <button
                      type="button"
                      className="tape-recorder-hit-btn"
                      aria-label={playoutPhase === 'playing' ? 'Pause' : 'Play'}
                      aria-pressed={
                        playoutPhase === 'playing' ||
                        playoutPhase === 'rewinding' ||
                        emptyTransportFlash === 'play'
                      }
                      onPointerDown={transportTapPointerDown}
                      onPointerUp={transportTapPointerUp}
                      onPointerCancel={transportTapPointerUp}
                      onClick={() => void onPlayClick()}
                    />
                  </td>
                </tr>
                <tr className="tape-recorder-slice-row" style={{ height: rowPct(105) }}>
                  <td className="tape-recorder-slice-cell tape-recorder-slice-cell--inert" rowSpan={2} />
                </tr>
                <tr className="tape-recorder-slice-row" style={{ height: rowPct(109) }}>
                  <td className="tape-recorder-slice-cell tape-recorder-slice-cell--inert" colSpan={2} />
                </tr>
              </tbody>
            </table>
          </div>
        </div>
        </div>
      </div>
      </div>
      <audio
        ref={forwardAudioRef}
        className="voice-note-forward-audio"
        preload="none"
        playsInline
      />
      </div>
      {hasBottomSubmitZone ? (
        <div className="voice-note-page__submit" aria-live="polite">
          <div className="voice-note-page__submit-inner">
            {recRecordPromptPhase !== 'off' ? (
              <p
                className={
                  'voice-note-page__rec-prompt' +
                  (recRecordPromptPhase === 'visible' ? ' voice-note-page__rec-prompt--visible' : '') +
                  (recRecordPromptPhase === 'out' ? ' voice-note-page__rec-prompt--out' : '')
                }
              >
                Press REC to record a note.
              </p>
            ) : null}
            {showSendVoiceNote ? (
              <>
                <button
                  type="button"
                  className={
                    'voice-note-page__send-btn' +
                    (sendBtnEntered ? ' voice-note-page__send-btn--entered' : '')
                  }
                  disabled={isMicRecording || uploadStatus === 'sending' || uploadStatus === 'sent'}
                  onClick={() => void onSendVoiceNote()}
                >
                  {uploadStatus === 'sending'
                    ? 'Sending…'
                    : uploadStatus === 'sent'
                      ? 'Thank you, we got it!'
                      : 'Send voice note'}
                </button>
                {uploadStatus === 'error' && uploadError ? (
                  <p className="voice-note-page__submit-msg voice-note-page__submit-msg--err">{uploadError}</p>
                ) : null}
              </>
            ) : null}
          </div>
        </div>
      ) : null}
    </div>
  );
}
